import React, { useState, useEffect, useRef } from 'react';
import { WHATSAPP_NUM } from '../constants';


// ---------------------------------------------------------------------------
// 🔐 Security config  –  add these to your .env file:
//   REACT_APP_API_KEY=your-secret-token-here
//   REACT_APP_RECAPTCHA_SITE_KEY=your-recaptcha-site-key
// ---------------------------------------------------------------------------
const API_KEY            = process.env.REACT_APP_API_KEY            || '';
const RECAPTCHA_SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY || '';

const CLASSES = [ '5th-9th',
  '10th', 'Intermediate (11th)', 'Intermediate (12th)', 'Diploma',
  'Degree – 1st Year', 'Degree – 2nd Year', 'Degree – 3rd Year', 'Other',
];

const COURSES = [
  'Python + AI Course',
  'Web Development Course',
  'Both Courses (Combo)',
  'Not sure yet — guide me',
];

const TIMINGS = ['Morning (9AM – 12PM)', 'Afternoon (1PM – 4PM)', 'Evening (5PM – 8PM)'];

// ---------------------------------------------------------------------------
// Generate next 6 days starting from tomorrow
// ---------------------------------------------------------------------------
function getNext6Days() {
  const days = [];
  const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
                      'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

  for (let i = 1; i <= 6; i++) {
    const d = new Date();
    d.setDate(d.getDate() + i);
    const dayName  = dayNames[d.getDay()];
    const date     = d.getDate();
    const month    = monthNames[d.getMonth()];
    const year     = d.getFullYear();
    const value    = `${dayName}, ${date} ${month} ${year}`;   // stored in form
    days.push({ value, dayName, date, month });
  }
  return days;
}

function Field({ label, error, children }) {
  return (
    <div className="form-group">
      <label className="form-label">{label}</label>
      {children}
      {error && <div className="form-error">⚠️ {error}</div>}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Inject reCAPTCHA v3 script once
// ---------------------------------------------------------------------------
function useRecaptcha() {
  const loaded = useRef(false);

  useEffect(() => {
    if (!RECAPTCHA_SITE_KEY || loaded.current) return;
    const script = document.createElement('script');
    script.src = `https://www.google.com/recaptcha/api.js?render=${RECAPTCHA_SITE_KEY}`;
    script.async = true;
    document.head.appendChild(script);
    loaded.current = true;
  }, []);

  const getToken = (action) =>
    new Promise((resolve, reject) => {
      if (!RECAPTCHA_SITE_KEY) return resolve(null);
      if (!window.grecaptcha) return reject(new Error('reCAPTCHA not loaded'));
      window.grecaptcha.ready(() => {
        window.grecaptcha
          .execute(RECAPTCHA_SITE_KEY, { action })
          .then(resolve)
          .catch(reject);
      });
    });

  return { getToken };
}

// ---------------------------------------------------------------------------
export default function DemoPage() {
  const [form, setForm] = useState({
    name: '', parent: '', phone: '', email: '',
    cls: '', laptop: '', course: '', demoDate: '', timing: '', note: '',
  });
  const [errors, setErrors]   = useState({});
  const [status, setStatus]   = useState('idle'); // idle | sending | success | error

  const { getToken } = useRecaptcha();
  const next6Days    = getNext6Days();

  const set = (key) => (e) => setForm(f => ({ ...f, [key]: e.target.value }));

  function validate() {
    const e = {};
    if (!form.name.trim())   e.name     = 'Student name is required';
    if (!form.parent.trim()) e.parent   = 'Parent / guardian name is required';
    if (!form.phone.trim() || form.phone.replace(/\D/g, '').length < 10)
                             e.phone    = 'Enter a valid 10-digit phone number';
    if (!form.email.trim() || !form.email.includes('@'))
                             e.email    = 'Valid email address required';
    if (!form.cls)           e.cls      = 'Please select current class';
    if (!form.laptop)        e.laptop   = 'Please select an option';
    if (!form.course)        e.course   = 'Please select a course';
    if (!form.demoDate)      e.demoDate = 'Please select a preferred demo date';
    return e;
  }

  async function handleSubmit() {
    const e = validate();
    if (Object.keys(e).length > 0) { setErrors(e); return; }

    setStatus('sending');
    setErrors({});

    try {
      const recaptchaToken = await getToken('demo_registration');

      const sharedParams = {
        student_name:    form.name,
        parent_name:     form.parent,
        phone:           form.phone,
        email:           form.email,
        cls:             form.cls,
        laptop:          form.laptop,
        course:          form.course,
        demo_date:       form.demoDate,
        timing:          form.timing  || 'Flexible',
        note:            form.note    || 'None',
        submitted_at:    new Date().toLocaleString('en-IN'),
        recaptchaToken,
      };

      const res = await fetch(
        'https://us-central1-remote-camera-control.cloudfunctions.net/sendDemoEmail',
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'x-api-key': API_KEY,
          },
          body: JSON.stringify(sharedParams),
        }
      );

      if (!res.ok) throw new Error('Failed');
      setStatus('success');

    } catch (err) {
      console.error(err);
      setStatus('error');
    }
  }

  // ── Success screen ───────────────────────────────────────────────────────
  if (status === 'success') {
    const waMsg =
      `Hi! I just registered for a free demo at VRK Tech Academy.\n` +
      `Name: ${form.name}\nPhone: ${form.phone}\nClass: ${form.cls}\n` +
      `Course: ${form.course}\nPreferred Date: ${form.demoDate}\n` +
      `Preferred Timing: ${form.timing || 'Flexible'}`;
    return (
      <div style={{
        paddingTop: 64, minHeight: '80vh',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        background: 'var(--cream)',
      }}>
        <div style={{ textAlign: 'center', padding: '40px 24px', maxWidth: 540 }}>
          <div style={{ fontSize: 70, marginBottom: 16 }}>🎉</div>
          <h2 style={{ color: 'var(--navy)', fontSize: 30, fontWeight: 900, marginBottom: 12 }}>
            You're Registered!
          </h2>
          <p style={{ color: 'var(--gray)', fontSize: 16, lineHeight: 1.75, marginBottom: 32 }}>
            Thank you, <strong>{form.name}</strong>! A confirmation has been sent to{' '}
            <strong>{form.email}</strong>. We'll also call you on{' '}
            <strong>{form.phone}</strong> within <strong>24 hours</strong> to confirm your demo
            slot on <strong>{form.demoDate}</strong>.
          </p>
          <div style={{ background: 'var(--navy)', borderRadius: 20, padding: 24, marginBottom: 24 }}>
            <p style={{ color: 'rgba(255,255,255,0.8)', fontSize: 14, marginBottom: 16 }}>
              For faster confirmation, send us a WhatsApp message now:
            </p>
            <button
              className="btn-gold"
              style={{ width: '100%', fontSize: 15, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
              onClick={() => window.open(`https://wa.me/${WHATSAPP_NUM}?text=${encodeURIComponent(waMsg)}`, '_blank')}
            >
              <img src="/whatsapp.png" alt="WhatsApp" style={{ height: 20, marginRight: 8 }} />
              Send WhatsApp Message
            </button>
          </div>
          <button
            className="btn-secondary"
            onClick={() => {
              setStatus('idle');
              setForm({ name: '', parent: '', phone: '', email: '', cls: '', laptop: '', course: '', demoDate: '', timing: '', note: '' });
            }}
          >
            Register Another Student
          </button>
        </div>
      </div>
    );
  }

  // ── Page ─────────────────────────────────────────────────────────────────
  return (
    <div style={{ paddingTop: 64 }}>

      {/* Header */}
      <section style={{
        background: 'linear-gradient(135deg, #0d2045, #1B3A6B, #2A5298)',
        padding: '68px 24px 52px', textAlign: 'center', color: '#fff',
      }}>
        <div className="badge" style={{
          background: 'rgba(240,180,41,0.2)', color: '#F0B429',
          border: '1.5px solid rgba(240,180,41,0.38)',
        }}>
          🆓 100% Free — No Payment Required
        </div>
        <h1 className="serif" style={{
          fontSize: 'clamp(28px, 5vw, 46px)', fontWeight: 800, margin: '16px 0 14px',
        }}>
          Book Your Free Demo Class
        </h1>
        <p style={{
          color: 'rgba(255,255,255,0.82)', fontSize: 17,
          maxWidth: 520, margin: '0 auto', lineHeight: 1.7,
        }}>
          Experience a real class for FREE. Meet the instructor, ask questions, and decide
          at your own pace — no pressure.
        </p>
        <div style={{ display: 'flex', gap: 20, justifyContent: 'center', flexWrap: 'wrap', marginTop: 32 }}>
          {['🎯 Live session', '⏱ 60 minutes', '💰 Zero cost', '🤝 No commitment'].map(t => (
            <span key={t} style={{
              background: 'rgba(255,255,255,0.12)', padding: '8px 18px',
              borderRadius: 50, fontSize: 14, fontWeight: 700,
              border: '1px solid rgba(255,255,255,0.2)',
            }}>{t}</span>
          ))}
        </div>
      </section>

      {/* Form */}
      <section className="section" style={{ background: 'var(--cream)' }}>
        <div className="container-sm">
          <div className="card" style={{ padding: '44px 40px' }}>
            <h2 style={{
              color: 'var(--navy)', fontWeight: 900, fontSize: 22,
              marginBottom: 6, textAlign: 'center',
            }}>
              📋 Registration Form
            </h2>
            <p style={{ color: 'var(--gray)', textAlign: 'center', marginBottom: 32, fontSize: 14 }}>
              Fill in the details below and we'll confirm your slot within 24 hours.
            </p>

            {status === 'error' && (
              <div style={{
                background: '#fff0f0', border: '1.5px solid #f5c6c6',
                borderRadius: 10, padding: '12px 16px', marginBottom: 20,
                color: '#c0392b', fontSize: 14,
              }}>
                ⚠️ Something went wrong. Please try again or WhatsApp us directly.
              </div>
            )}

            <Field label="Student Name *" error={errors.name}>
              <input className="form-input" placeholder="Enter student's full name"
                value={form.name} onChange={set('name')} />
            </Field>

            <Field label="Parent / Guardian Name *" error={errors.parent}>
              <input className="form-input" placeholder="Enter parent's full name"
                value={form.parent} onChange={set('parent')} />
            </Field>

            <Field label="WhatsApp Phone Number *" error={errors.phone}>
              <input className="form-input" type="tel" maxLength={10}
                placeholder="10-digit mobile number"
                value={form.phone} onChange={set('phone')} />
            </Field>

            <Field label="Email Address *" error={errors.email}>
              <input className="form-input" type="email"
                placeholder="your@email.com (for confirmation)"
                value={form.email} onChange={set('email')} />
            </Field>

            <Field label="Current Class / Education *" error={errors.cls}>
              <select className="form-input" value={form.cls} onChange={set('cls')}>
                <option value="">Select class</option>
                {CLASSES.map(c => <option key={c}>{c}</option>)}
              </select>
            </Field>

            <Field label="Do you have a Laptop/Computer? *" error={errors.laptop}> Laptop/Computer is required to attend the demo and regular classes. If you don't have one, select the option that best describes your situation.
              <select className="form-input" value={form.laptop} onChange={set('laptop')}>
                <option value="">Select</option>
                <option>Yes, I have.</option>
                <option>No, I don't have one yet</option>
                <option>I will arrange one before joining</option>
              </select>
            </Field>

            <Field label="Course Interested In *" error={errors.course}>
              <select className="form-input" value={form.course} onChange={set('course')}>
                <option value="">Select a course</option>
                {COURSES.map(c => <option key={c}>{c}</option>)}
              </select>
            </Field>

            {/* ── Preferred Demo Date ── */}
            <Field label="Preferred Demo Date *" error={errors.demoDate}>
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(3, 1fr)',
                gap: 10,
                marginTop: 4,
              }}>
                {next6Days.map(({ value, dayName, date, month }) => {
                  const selected = form.demoDate === value;
                  return (
                    <button
                      key={value}
                      type="button"
                      onClick={() => setForm(f => ({ ...f, demoDate: value }))}
                      style={{
                        padding: '10px 6px',
                        borderRadius: 10,
                        border: selected
                          ? '2px solid var(--navy, #1B3A6B)'
                          : '2px solid #d8dde6',
                        background: selected ? 'var(--navy, #1B3A6B)' : '#fff',
                        color: selected ? '#fff' : '#333',
                        cursor: 'pointer',
                        textAlign: 'center',
                        transition: 'all 0.15s ease',
                        fontFamily: 'inherit',
                      }}
                    >
                      <div style={{ fontSize: 11, fontWeight: 600, opacity: selected ? 0.85 : 0.55, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                        {dayName.slice(0, 3)}
                      </div>
                      <div style={{ fontSize: 22, fontWeight: 800, lineHeight: 1.2 }}>
                        {date}
                      </div>
                      <div style={{ fontSize: 12, fontWeight: 600, opacity: selected ? 0.85 : 0.6 }}>
                        {month}
                      </div>
                    </button>
                  );
                })}
              </div>
            </Field>

            <Field label="Preferred Demo Timing (Optional)">
              <select className="form-input" value={form.timing} onChange={set('timing')}>
                <option value="">Select preferred time</option>
                {TIMINGS.map(t => <option key={t}>{t}</option>)}
              </select>
            </Field>

            <Field label="Any questions or notes? (Optional)">
              <textarea className="form-input" rows={3}
                placeholder="e.g. I want to learn AI, do I need any background?"
                value={form.note} onChange={set('note')}
                style={{ resize: 'vertical' }} />
            </Field>

            <button
              className="btn-primary"
              style={{
                width: '100%', padding: '16px', fontSize: 16, marginTop: 8,
                opacity: status === 'sending' ? 0.7 : 1,
                cursor: status === 'sending' ? 'not-allowed' : 'pointer',
              }}
              onClick={handleSubmit}
              disabled={status === 'sending'}
            >
              {status === 'sending' ? '⏳ Sending…' : '📅 Book Free Demo Now'}
            </button>

            <div style={{ textAlign: 'center', marginTop: 16, color: 'var(--gray)', fontSize: 13 }}>
              🔒 Your information is safe. We'll only contact you about the demo session.
            </div>
          </div>
        </div>
      </section>

      {/* FAQ strip */}
      <section className="section-sm" style={{ background: 'var(--navy)' }}>
        <div className="container">
          <div style={{ textAlign: 'center', marginBottom: 28 }}>
            <h3 style={{ color: '#fff', fontWeight: 800, fontSize: 20 }}>Frequently Asked Questions</h3>
          </div>
          <div className="grid-3">
            {[
              ['Is the demo really free?', 'Yes, 100% free. No credit card, no payment. Just show up and learn.'],
              ["What if I can't attend the first slot?", "No problem! We'll reschedule to a time that works for you."],
              ['Do I need any prior coding knowledge?', 'Absolutely not. Our courses start from the very basics.'],
            ].map(([q, a]) => (
              <div key={q} style={{ background: 'rgba(255,255,255,0.07)', borderRadius: 16, padding: 24 }}>
                <div style={{ fontWeight: 800, color: '#F0B429', marginBottom: 8, fontSize: 15 }}>{q}</div>
                <div style={{ color: 'rgba(255,255,255,0.75)', fontSize: 14, lineHeight: 1.65 }}>{a}</div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}